import React from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Share2, Twitter, Facebook, Link } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ShareButton = ({ 
  nickname, 
  avatarId, 
  totalScore = 0, 
  gamesPlayed = 0, 
  accuracy = 0,
  variant = "default",
  size = "default",
  showLabel = true,
  className = ""
}) => {
  const { toast } = useToast();

  const shareUrl = `https://jailbirdsgame.com?ref=share&player=${encodeURIComponent(nickname || 'Player')}`;
  const shareText = `🐦 Just flew through JailBirds and scored ${(totalScore || 0).toLocaleString()} points! Think you can ruffle my feathers? 🚔 Join the flock and see if you can beat my score! 🎯`;

  const handleTwitterShare = async () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
    
    toast({
      title: "Sharing to Twitter",
      description: "Opening Twitter share dialog...",
    });
  };

  const handleFacebookShare = async () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(facebookUrl, '_blank', 'width=550,height=420');
    
    toast({
      title: "Sharing to Facebook",
      description: "Opening Facebook share dialog...",
    });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast({
        title: "Link Copied!",
        description: "Share link copied to clipboard.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy link to clipboard.",
        variant: "destructive",
      });
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      toast({
        title: "Not Supported",
        description: "Native sharing is not supported on this device.",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.share({
        title: 'JailBirds Game',
        text: shareText,
        url: shareUrl,
      });
      
      toast({
        title: "Shared!",
        description: "Thanks for spreading the word! 🐦",
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        toast({
          title: "Error",
          description: "Failed to share. Please try another method.",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size}
          className={className}
        >
          <Share2 className="w-4 h-4 mr-2" />
          {showLabel && "Share"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 z-[300]">
        {navigator.share && (
          <DropdownMenuItem onClick={handleNativeShare}>
            <Share2 className="w-4 h-4 mr-2" />
            Share...
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleTwitterShare}>
          <Twitter className="w-4 h-4 mr-2" />
          Share on Twitter
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleFacebookShare}>
          <Facebook className="w-4 h-4 mr-2" />
          Share on Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCopyLink}>
          <Link className="w-4 h-4 mr-2" />
          Copy Link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ShareButton;
